// Type Imports
import type { CrudFilter, CrudSort, DataProvider, LogicalFilter } from '@refinedev/core'

// Third-party Imports
import { MODULE_MANIFESTS, buildResourcePathIndex } from '@coresoft/platform-contract'

// API Imports
import { API_URL, apiFetch } from '@/lib/api/client'

import type { Paginated } from '@/types/api'

type QueryParams = Record<string, string | number | boolean | undefined>

/*
 * Refine-ийн resource нэр → API зам.
 *
 * ! Нэр нь ЦЭГТЭЙ (`hr.employees`) — эрхийн нэрийн зайтай нийцүүлсэн. API зам нь
 * ! ЗУРААСТАЙ (`hr/employees`). Хөрвүүлэхгүй бол `/api/hr.employees` гэсэн
 * ! байхгүй зам үүсэх тул модулийн дэлгэцүүд Refine-ийн resource давхаргыг
 * ! ашиглаж чадахгүй байв.
 *
 * Манифестад байхгүй нэр ирвэл өөрийг нь замд ашиглана — гуравдагч кодыг
 * хугалахгүйн тулд.
 */
const RESOURCE_PATHS = buildResourcePathIndex(MODULE_MANIFESTS)

const pathOf = (resource: string): string => RESOURCE_PATHS.get(resource) ?? resource

/*
 * The API exposes ONE free-text parameter (`q`) per resource plus a handful of
 * named equality filters, so `contains` on any field folds into `q` and `eq`/`in`
 * become named params. Anything else is dropped rather than silently mistranslated
 * into a filter the server would ignore — the warning says which one.
 */
const applyFilters = (params: QueryParams, filters: CrudFilter[] = []): void => {
  for (const filter of filters) {
    if (filter.operator === 'or' || filter.operator === 'and') {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[dataProvider] "${filter.operator}" filters are not supported by the API`)
      }

      continue
    }

    const { field, operator, value } = filter as LogicalFilter

    if (value === undefined || value === null || value === '') continue

    switch (operator) {
      case 'contains':
      case 'containss':
        params.q = String(value)
        break
      case 'eq':
        params[field] = String(value)
        break
      case 'in':
        params[field] = (Array.isArray(value) ? value : [value]).join(',')
        break

      default:
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[dataProvider] unsupported operator "${operator}" on "${field}"`)
        }
    }
  }
}

const applySorters = (params: QueryParams, sorters: CrudSort[] = []): void => {
  const [primary] = sorters

  // The API sorts on a single column; a secondary sorter would be silently
  // ignored server-side, so only the first is sent.
  if (primary) {
    params.sort = primary.field
    params.order = primary.order
  }
}

export const dataProvider: DataProvider = {
  getApiUrl: () => API_URL,

  getList: async ({ resource, pagination, sorters, filters, meta }) => {
    const params: QueryParams = {
      page: pagination?.currentPage ?? 1,
      pageSize: pagination?.pageSize ?? 20,
      ...(meta?.query as QueryParams | undefined)
    }

    applySorters(params, sorters)
    applyFilters(params, filters)

    const result = await apiFetch<Paginated<never>>(`/${pathOf(resource)}`, { query: params })

    return { data: result.data, total: result.total }
  },

  getOne: async ({ resource, id }) => {
    const data = await apiFetch<never>(`/${pathOf(resource)}/${id}`)

    return { data }
  },

  // The API has no bulk-read route; for the handful of ids a relation field asks
  // for, parallel single reads are cheaper than adding one.
  getMany: async ({ resource, ids }) => {
    const data = await Promise.all(ids.map(id => apiFetch<never>(`/${pathOf(resource)}/${id}`)))

    return { data }
  },

  create: async ({ resource, variables }) => {
    const data = await apiFetch<never>(`/${pathOf(resource)}`, { method: 'POST', body: variables })

    return { data }
  },

  update: async ({ resource, id, variables }) => {
    const data = await apiFetch<never>(`/${pathOf(resource)}/${id}`, { method: 'PATCH', body: variables })

    return { data }
  },

  deleteOne: async ({ resource, id }) => {
    await apiFetch<void>(`/${pathOf(resource)}/${id}`, { method: 'DELETE' })

    return { data: { id } as never }
  },

  custom: async ({ url, method, payload, query, headers }) => {
    const data = await apiFetch<never>(url, {
      method: method.toUpperCase(),
      ...(payload === undefined ? {} : { body: payload }),
      query: query as QueryParams | undefined,
      headers: headers as HeadersInit | undefined
    })

    return { data }
  }
}
